import { Link } from 'react-router-dom';
import { usePosts } from '@/hooks/usePosts';

// ─────────────────────────────────────────────────────────────────────────────
// AlertTicker
//
// Fixed 28px bar at the very top of the viewport.
// Scrolls the latest 10 post titles in a CSS marquee.
// Pauses on hover via .marquee-track:hover rule in index.css.
// ─────────────────────────────────────────────────────────────────────────────

export default function AlertTicker() {
  const { data } = usePosts({ limit: 10, sort: 'latest' });
  const posts    = data?.data ?? [];

  if (!posts.length) {
    // Reserve space even while loading to avoid layout shift
    return (
      <div
        className="fixed left-0 right-0 top-0 z-50 flex h-7 items-center overflow-hidden border-b border-red-500/30 bg-bg-950"
        aria-hidden
      />
    );
  }

  // Duplicate items so the loop is seamless
  const items = [...posts, ...posts];

  return (
    <div
      className="fixed left-0 right-0 top-0 z-50 flex h-7 items-center overflow-hidden border-b border-red-500/30 bg-bg-950 group"
      role="marquee"
      aria-label="Latest incidents ticker"
    >
      {/* Static prefix label */}
      <div className="flex shrink-0 items-center gap-2 border-r border-red-500/30 bg-red-500/10 px-3 h-full">
        <span className="font-mono text-[11px] font-semibold text-red-400 whitespace-nowrap">
          ⚠ NEW INCIDENT:
        </span>
      </div>

      {/* Scrolling track */}
      <div className="relative flex flex-1 overflow-hidden">
        <div
          className="marquee-track flex items-center whitespace-nowrap"
          style={{ '--marquee-dur': `${Math.max(30, posts.length * 5)}s` } as React.CSSProperties}
        >
          {items.map((post, i) => (
            <span key={`${post.id}-${i}`} className="flex items-center">
              <Link
                to={`/posts/${post.slug}`}
                className="px-6 font-mono text-[11px] text-text-secondary hover:text-red-400 transition-colors"
              >
                {post.title}
              </Link>
              <span className="text-red-500/40 font-mono text-[11px]">◆</span>
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
